export interface FeatureAccess {
  isSuperAdmin: boolean;
  features: readonly string[];
  canAccess: (featureName?: string) => boolean;
}

export type FeatureMatch = "any" | "all";

const normalizeFeatureName = (name: string): string =>
  name.trim().toLowerCase().replace(/[\s_-]+/g, "");

export const normalizeRequiredFeatures = (requirements: unknown): string[] =>
  Array.isArray(requirements)
    ? Array.from(new Set(requirements.flatMap((requirement) => {
      if (typeof requirement !== "string") return [];
      const normalizedRequirement = normalizeFeatureName(requirement);
      return normalizedRequirement ? [normalizedRequirement] : [];
    })))
    : [];

export const canAccessAny = (
  access: Pick<FeatureAccess, "canAccess">,
  requirements: unknown,
): boolean => {
  const normalizedRequirements = normalizeRequiredFeatures(requirements);
  return normalizedRequirements.length === 0
    || normalizedRequirements.some((requirement) => access.canAccess(requirement));
};

export const canAccessAll = (
  access: Pick<FeatureAccess, "canAccess">,
  requirements: unknown,
): boolean => normalizeRequiredFeatures(requirements)
  .every((requirement) => access.canAccess(requirement));

export const canAccessPage = (
  access: Pick<FeatureAccess, "canAccess">,
  requirements: unknown,
  featureMatch: FeatureMatch = "any",
): boolean => featureMatch === "all"
    ? canAccessAll(access, requirements)
    : canAccessAny(access, requirements);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const createFeatureAccess = (user: unknown): FeatureAccess => {
  const isSuperAdmin = isRecord(user) && user.iam_superadmin === true;
  const featureFlag = isRecord(user) && isRecord(user.feature_flag)
    ? user.feature_flag
    : undefined;
  const rawFeatures = featureFlag?.features;
  const features = Array.isArray(rawFeatures)
    ? Array.from(new Set(rawFeatures.flatMap((feature) => {
      if (!isRecord(feature) || typeof feature.name !== "string") {
        return [];
      }

      const normalizedName = normalizeFeatureName(feature.name);
      return normalizedName ? [normalizedName] : [];
    })))
    : [];
  const featureSet = new Set(features);

  return {
    isSuperAdmin,
    features,
    canAccess: (featureName?: string) => {
      const normalizedName = typeof featureName === "string" ? normalizeFeatureName(featureName) : "";
      return !normalizedName || isSuperAdmin || featureSet.has(normalizedName);
    },
  };
};
